// Render-only trajectory history.
//
// Trails record the coordinates an object actually visited. They never feed
// back into collision or movement, so a ricochet stays a physical corner while
// an orbit naturally becomes a curve when its centre moves.

const DEFAULT_BANDS = Object.freeze([
  { from: 0.00, to: 0.42, width: 0.28, alpha: 0.22 },
  { from: 0.32, to: 0.76, width: 0.62, alpha: 0.56 },
  { from: 0.66, to: 1.00, width: 1.00, alpha: 1.00 },
]);

export class TrajectoryTrail {
  constructor(capacity = 14, maxSegment = 14, maxInsert = 5) {
    this.capacity = Math.max(3, capacity | 0);
    this.maxSegment = Math.max(1, maxSegment);
    this.maxInsert = Math.max(1, maxInsert | 0);
    this.x = new Float32Array(this.capacity);
    this.y = new Float32Array(this.capacity);
    this.time = new Float32Array(this.capacity);
    this.sharp = new Uint8Array(this.capacity);
    this.renderX = new Float32Array(this.capacity * 3 + 4);
    this.renderY = new Float32Array(this.capacity * 3 + 4);
    this.renderCount = 0;
    this.renderVersion = -1;
    this.renderSmooth = false;
    this.version = 0;
    this.start = 0;
    this.count = 0;
  }

  reset() {
    this.start = 0;
    this.count = 0;
    this.version++;
  }

  _slot(index) {
    return (this.start + index) % this.capacity;
  }

  getX(index) {
    return this.x[this._slot(index)];
  }

  getY(index) {
    return this.y[this._slot(index)];
  }

  getTime(index) {
    return this.time[this._slot(index)];
  }

  isSharp(index) {
    return this.sharp[this._slot(index)] !== 0;
  }

  _append(x, y, time, sharp = false) {
    let slot;
    if (this.count < this.capacity) {
      slot = this._slot(this.count++);
    } else {
      this.start = (this.start + 1) % this.capacity;
      slot = this._slot(this.count - 1);
    }
    this.x[slot] = x;
    this.y[slot] = y;
    this.time[slot] = time;
    this.sharp[slot] = sharp ? 1 : 0;
    this.version++;
  }

  sample(x, y, time, sharp = false) {
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(time)) return;
    if (this.count === 0) {
      this._append(x, y, time, sharp);
      return;
    }

    const last = this._slot(this.count - 1);
    const x0 = this.x[last], y0 = this.y[last], t0 = this.time[last];
    const dx = x - x0, dy = y - y0;
    const distance = Math.hypot(dx, dy);

    // A motionless head should stay current without filling the ring with
    // duplicate points. Preserve a collision marker if this sample is sharp.
    if (distance < 0.08) {
      this.x[last] = x;
      this.y[last] = y;
      this.time[last] = time;
      if (sharp) this.sharp[last] = 1;
      this.version++;
      return;
    }

    // Fast-forward/debug speeds can move farther than one useful segment in a
    // frame. Insert a bounded number of points so the visible path remains the
    // route travelled rather than one giant chord.
    const steps = Math.min(this.maxInsert, Math.max(1, Math.ceil(distance / this.maxSegment)));
    for (let i = 1; i <= steps; i++) {
      const u = i / steps;
      this._append(
        x0 + dx * u,
        y0 + dy * u,
        t0 + (time - t0) * u,
        sharp && i === steps,
      );
    }
  }

  trim(now, maxAge, maxLength = Infinity) {
    this.version++;
    const cutoff = now - Math.max(0, maxAge);
    // Keep the point immediately before the age boundary so the visible line
    // does not pop away one whole segment at a time.
    while (this.count > 2 && this.getTime(1) < cutoff) {
      this.start = (this.start + 1) % this.capacity;
      this.count--;
    }
    if (this.count >= 2 && this.getTime(0) < cutoff) {
      const t0 = this.getTime(0), t1 = this.getTime(1);
      if (t1 <= cutoff) {
        this.start = (this.start + 1) % this.capacity;
        this.count--;
      } else {
        const u = Math.max(0, Math.min(1, (cutoff - t0) / Math.max(1e-6, t1 - t0)));
        const first = this._slot(0);
        this.x[first] += (this.getX(1) - this.x[first]) * u;
        this.y[first] += (this.getY(1) - this.y[first]) * u;
        this.time[first] = cutoff;
        this.sharp[first] = 0;
      }
    }

    if (!Number.isFinite(maxLength) || maxLength <= 0 || this.count < 2) return;
    let accumulated = 0;
    for (let i = this.count - 2; i >= 0; i--) {
      const x0 = this.getX(i), y0 = this.getY(i);
      const x1 = this.getX(i + 1), y1 = this.getY(i + 1);
      const segment = Math.hypot(x1 - x0, y1 - y0);
      if (accumulated + segment <= maxLength) {
        accumulated += segment;
        continue;
      }

      const u = segment > 0 ? Math.max(0, (maxLength - accumulated) / segment) : 0;
      const clippedX = x1 + (x0 - x1) * u;
      const clippedY = y1 + (y0 - y1) * u;
      const clippedT = this.getTime(i + 1) + (this.getTime(i) - this.getTime(i + 1)) * u;
      if (i > 0) {
        this.start = this._slot(i);
        this.count -= i;
      }
      const first = this._slot(0);
      this.x[first] = clippedX;
      this.y[first] = clippedY;
      this.time[first] = clippedT;
      this.sharp[first] = 0;
      break;
    }
  }
}

function pushSmooth(outX, outY, x, y, count) {
  if (count >= outX.length) return count;
  if (count > 0) {
    const dx = outX[count - 1] - x;
    const dy = outY[count - 1] - y;
    if (dx * dx + dy * dy < 0.0025) return count;
  }
  outX[count] = x;
  outY[count] = y;
  return count + 1;
}

function smoothTrail(trail, smooth) {
  if (trail.renderVersion === trail.version && trail.renderSmooth === smooth) {
    return trail.renderCount;
  }
  const outX = trail.renderX;
  const outY = trail.renderY;
  const n = trail.count;
  if (n < 2) {
    trail.renderCount = 0;
    trail.renderVersion = trail.version;
    trail.renderSmooth = smooth;
    return 0;
  }
  if (!smooth || n === 2) {
    const cap = Math.min(n, outX.length);
    for (let i = 0; i < cap; i++) {
      outX[i] = trail.getX(i);
      outY[i] = trail.getY(i);
    }
    trail.renderCount = cap;
    trail.renderVersion = trail.version;
    trail.renderSmooth = smooth;
    return cap;
  }

  // One corner-cut pass is enough at this scale. Sharp samples split the pass,
  // keeping a ricochet kink while orbit and steering samples become fluid.
  let out = 0;
  let start = 0;
  while (start < n - 1 && out < outX.length) {
    let end = start + 1;
    while (end < n - 1 && !trail.isSharp(end)) end++;
    out = pushSmooth(outX, outY, trail.getX(start), trail.getY(start), out);
    for (let i = start; i < end && out < outX.length - 2; i++) {
      const x0 = trail.getX(i), y0 = trail.getY(i);
      const x1 = trail.getX(i + 1), y1 = trail.getY(i + 1);
      out = pushSmooth(
        outX, outY,
        x0 * 0.75 + x1 * 0.25,
        y0 * 0.75 + y1 * 0.25,
        out,
      );
      out = pushSmooth(
        outX, outY,
        x0 * 0.25 + x1 * 0.75,
        y0 * 0.25 + y1 * 0.75,
        out,
      );
    }
    out = pushSmooth(outX, outY, trail.getX(end), trail.getY(end), out);
    start = end;
  }
  trail.renderCount = out;
  trail.renderVersion = trail.version;
  trail.renderSmooth = smooth;
  return out;
}

// Batched stepped ribbons: all trails sharing a style are submitted in only a
// handful of stroke calls. The three overlapping age bands create a tapered
// tail without allocating one Graphics object (or one spline) per projectile.
export function drawTrailBatch(graphics, trails, passes, {
  alphaScale = 1,
  widthScale = 1,
  smooth = true,
  bands = DEFAULT_BANDS,
  maxTrails = Infinity,
} = {}) {
  if (!graphics || !trails?.length || !passes?.length || alphaScale <= 0) return;
  const firstTrail = Math.max(0, trails.length - Math.max(0, maxTrails));

  for (const pass of passes) {
    for (const band of bands) {
      const from = Math.max(pass.start ?? 0, band.from);
      const to = Math.min(pass.end ?? 1, band.to);
      if (to <= from) continue;

      graphics.lineStyle(
        Math.max(0.35, pass.width * band.width * widthScale),
        pass.color,
        Math.min(1, pass.alpha * band.alpha * alphaScale),
      );
      graphics.beginPath();
      let paths = 0;
      for (let ti = firstTrail; ti < trails.length; ti++) {
        const trail = trails[ti]?.trail ?? trails[ti];
        const count = smoothTrail(trail, smooth);
        if (count < 2) continue;
        const pointsX = trail.renderX, pointsY = trail.renderY;
        const last = count - 1;
        const i0 = Math.max(0, Math.min(last - 1, Math.floor(from * last)));
        const i1 = Math.max(i0 + 1, Math.min(last, Math.ceil(to * last)));
        graphics.moveTo(pointsX[i0], pointsY[i0]);
        for (let i = i0 + 1; i <= i1; i++) graphics.lineTo(pointsX[i], pointsY[i]);
        paths++;
      }
      if (paths > 0) graphics.strokePath();
    }
  }
}
